import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import classnames from 'classnames';
import toast from 'react-hot-toast';
import routes from '@achievable-me/tundra-wizard/routes';
import { reverse } from 'named-urls';
import React, { Component } from 'react';
import USER_EXAM_STATUS from '@achievable-me/tundra-wizard/userExamStatus';
import { track } from '$lib/tracking';
import STEP from '$components/review/step';
import MODE from '$components/review/mode';
import ClipboardProvider from '$components/library/ClipboardProvider';
import logger from '$lib/logger';
import { parsePrompt } from '$lib/quiz';
import ExamTimer from './ExamTimer';
import styles from './styles.scss';

const SHORT_EXAM_MAP_SLUG_MAP = {
  'finra-sie': 'FINRA SIE',
  'finra-series-6': 'FINRA Series 6',
  'finra-series-7': 'FINRA Series 7',
  'finra-series-63': 'FINRA Series 63',
  'finra-series-65': 'FINRA Series 65',
  'finra-series-66': 'FINRA Series 66',
  gre: 'GRE',
  'amc-8': 'AMC 8',
  'amc-12': 'AMC 10/12',
  'usmle-step-1': 'USMLE Step 1',
};

const getShortExamName = (productSlug) => {
  return SHORT_EXAM_MAP_SLUG_MAP[productSlug] || productSlug;
};

const ReviewSubheaderSecondary = (props) => {
  const { handleShowOptions, mode, quizState } = props;
  const { examQueueIndex, quizQueue, step, product } = quizState;

  if (mode === MODE.essay) {
    const { essayResponse } = quizState;
    if (!essayResponse) {
      return null;
    }

    if (essayResponse.submitAt) {
      const submitAt = moment(essayResponse.submitAt).format('h:mma');
      return `Submit at ${submitAt}`;
    }

    if (step === STEP.essayWriting && essayResponse.updatedAt) {
      const autoSavedAt = moment(essayResponse.updatedAt).format('h:mma');
      return `Autosaved at ${autoSavedAt}`;
    }
  }

  if (mode === MODE.review && step === STEP.sessionSelect) {
    return (
      <i
        className={classnames('fad fa-sliders-v', styles.sliders)}
        onClick={handleShowOptions}
      />
    );
  }

  if (!_.includes([STEP.quizPrompt, STEP.quizConfirmation, STEP.quizExplanation], step)) {
    return null;
  }

  if (!quizQueue) {
    return null;
  }

  const quizObj = mode === MODE.exam ? quizQueue[examQueueIndex] : quizQueue[0];
  const { item, quiz } = quizObj;
  const { code } = item;
  const { seed, prompt } = quiz;

  const codeText = (() => {
    if (mode === MODE.review || mode === MODE.debug || mode === MODE.share) {
      return `${code}-${seed}`;
    }

    return `${examQueueIndex + 1} | ${code}-${seed}`;
  })();

  const { truncatedSlug } = parsePrompt({ code, prompt });

  const { slug: productSlug } = product;
  const { origin } = window.location;
  const shareRoute = reverse(routes.study.quiz, { productSlug, quizCode: code });
  const shareUrl = `${origin}${shareRoute}#seed=${seed}&slug=${truncatedSlug}`;

  return (
    <ClipboardProvider>
      {({ copy }) => {
        const onClick = () => {
          copy(shareUrl);
          toast.success('Copied URL to share quiz');
          track('Quiz | Shared', { productSlug, code });
        };

        return (
          <div
            data-aaa="shareQuiz"
            data-share-url={shareUrl}
            className={styles.codeInner}
            onClick={onClick}
          >
            <div className={styles.codeText}>{codeText}</div>
            <i className={classnames(styles.shareIcon, 'fad fa-share-square')} />
          </div>
        );
      }}
    </ClipboardProvider>
  );
};
class ReviewSubheader extends Component {
  renderTime = () => {
    const { mode, quizState } = this.props;
    const { step } = quizState;

    if (mode === MODE.essay) {
      if (!_.includes([STEP.essayWriting, STEP.essayReview], step)) {
        return null;
      }
      const { essayResponse } = quizState;
      const { startedAt, submitAt } = essayResponse;

      if (!startedAt) {
        return null;
      }

      if (submitAt) {
        const duration = moment.duration(submitAt - startedAt);
        const minutes = Math.floor(duration.asMinutes());
        const time = pluralize('minute', minutes, true);
        return time;
      }

      return <ExamTimer startedAt={startedAt} allottedTimeMins={30} />;
    }

    if (mode !== MODE.exam) {
      return null;
    }

    if (
      !_.includes(
        [STEP.examNavigation, STEP.examReview, STEP.examSummary, STEP.quizPrompt],
        step
      )
    ) {
      return null;
    }

    const { userExam } = quizState;
    if (!userExam || userExam.status !== USER_EXAM_STATUS.started) {
      return null;
    }

    const {
      exam: { allottedTimeMins },
      startedAt,
    } = userExam;
    return <ExamTimer startedAt={startedAt} allottedTimeMins={allottedTimeMins} />;
  };

  renderSubheader = () => {
    const { mode, quizState } = this.props;
    const { step } = quizState;

    switch (step) {
      case STEP.loading: {
        return 'Loading...';
      }
      case STEP.essayInstructions: {
        return 'Essay instructions';
      }
      case STEP.essayReview:
      case STEP.essayWriting: {
        return 'Essay';
      }
      case STEP.examNavigation: {
        return 'Exam navigation';
      }
      case STEP.examReview:
      case STEP.examSummary: {
        return 'Exam summary';
      }
      case STEP.sessionOptions: {
        return 'Session options';
      }
      case STEP.sessionSelect: {
        return 'Session overview';
      }
      case STEP.quizPrompt:
      case STEP.quizConfirmation: {
        if (mode === MODE.exam) {
          return 'Exam';
        }

        if (mode === MODE.share || mode === MODE.debug) {
          const { product } = quizState;
          const { slug: productSlug } = product;
          const shortExamName = getShortExamName(productSlug);
          return `${shortExamName} quiz`;
        }

        return 'Quiz';
      }
      case STEP.quizExplanation: {
        return 'Explanation';
      }
      case STEP.summary: {
        return 'Summary';
      }
      default: {
        logger.warn(`Unknown quizState: ${step}`);
        return null;
      }
    }
  };

  render() {
    const { handleShowOptions, mode, quizState } = this.props;

    return (
      <div className={styles.layout}>
        <div className={styles.subheader} data-aaa="reviewSubheader">
          {this.renderSubheader()}
        </div>
        {this.renderTime()}
        <div className={styles.code} data-aaa="reviewCode">
          <ReviewSubheaderSecondary
            handleShowOptions={handleShowOptions}
            mode={mode}
            quizState={quizState}
          />
        </div>
      </div>
    );
  }
}

export default ReviewSubheader;
